import { useEffect, useRef } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const Partners = () => {
  const { t } = useLanguage();
  const carouselRef = useRef<HTMLDivElement>(null);

  const partners = [
    { name: "Partner 1", logo: "/lovable-uploads/492fb774-4497-4088-ac4e-e989ddc9a705.png" },
    { name: "Belmazad", logo: "/lovable-uploads/3e8c0475-bb88-454a-9940-cbb5681d009f.png" },
    { name: "Partner 3", logo: "/lovable-uploads/10719c91-7041-46c6-ac34-9942726f8560.png" },
    { name: "Partner 4", logo: "/lovable-uploads/9f64ba44-bd3e-445e-93b0-a3d77916ccf9.png" },
    { name: "Partner 5", logo: "/lovable-uploads/4e1fa34f-d449-492d-9141-dc65af68cce3.png" },
    { name: "Partner 6", logo: "/lovable-uploads/c0fd16fe-8301-4a66-b7be-452f1d990d21.png" },
    { name: "Partner 7", logo: "/lovable-uploads/1581564d-cb64-4ee8-be7b-1ed68d2add3a.png" },
    { name: "Marakez", logo: "/lovable-uploads/78c0e5d7-346d-4d8d-b545-459e8dc3dc6b.png" },
    { name: "Partner 9", logo: "/lovable-uploads/55063c45-c101-407e-8440-68928dfbe971.png" },
    { name: "Al Ahly Pharos", logo: "/lovable-uploads/3c56e973-ff9b-42d3-ba6e-e63500b24817.png" },
    { name: "Partner 11", logo: "/lovable-uploads/8440f8fb-64c2-43f7-b3d2-f163a3462991.png" },
    { name: "Partner 12", logo: "/lovable-uploads/f8a0ab52-ff78-42e8-b1f9-877f5effc490.png" },
    { name: "Partner 13", logo: "/lovable-uploads/0da9b2b1-5f33-4051-b254-c4e200cebfb8.png" },
    { name: "Partner 14", logo: "/lovable-uploads/303e6592-fea1-454c-ab79-358c8f7cb618.png" },
  ];

  // Shuffle function to randomize partners order
  const shuffleArray = (array: typeof partners) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Create seamless infinite loop by tripling the array
  const shuffledPartners = shuffleArray(partners);
  const infinitePartners = [...shuffledPartners, ...shuffledPartners, ...shuffledPartners];

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Start from the beginning (left)
    const itemWidth = 328; // 320px width + 8px gap  
    const totalItems = shuffledPartners.length;
    carousel.scrollLeft = 0;

    let animationId: number;
    const scrollSpeed = 1;

    const scroll = () => {
      carousel.scrollLeft += scrollSpeed;
      
      // When we've scrolled past the first set, reset to beginning
      const maxScroll = totalItems * itemWidth;
      if (carousel.scrollLeft >= maxScroll) {
        carousel.scrollLeft = 0;
      }
      
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [shuffledPartners.length]);

  return (
    <section id="partners" className="bg-white mx-0 py-[2px]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-4 sm:mb-6 md:mb-8 mt-6 sm:mt-8 md:mt-12 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-black mb-3 sm:mb-4 animate-fade-in">
            {t("partners.title")}
          </h2>
          <p className="text-xs sm:text-sm md:text-base text-black max-w-3xl mx-auto animate-fade-in">
            {t("partners.subtitle")}
          </p>
        </div>

        <div className="w-full overflow-hidden bg-[#000000] rounded-lg">
          <div 
            ref={carouselRef}
            className="flex gap-4 overflow-hidden animate-scroll-left"
          >
            {infinitePartners.map((partner, index) => (
              <div
                key={`${partner.name}-${index}`}
                className="flex-shrink-0 flex items-center justify-center p-2 sm:p-3 md:p-6 min-w-[160px] sm:min-w-[200px] md:min-w-[320px] h-[80px] sm:h-[100px] md:h-[160px]"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  width="280"
                  height="140"
                  loading="lazy"
                  className="w-[200px] h-[100px] sm:w-[240px] sm:h-[120px] md:w-[280px] md:h-[140px] object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-6 sm:mt-8 mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-black max-w-4xl mx-auto leading-relaxed animate-fade-in">
            {t("partners.description")}
          </p>
        </div>
      </div>
    </section>
  );
};
export default Partners;
